import jsPDF from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';
import { makeNiceName, getMountDisplayInfo } from './helpers';

// Apply the autoTable plugin to jsPDF
applyPlugin(jsPDF);

/**
 * PDF Report Helper - React equivalent of Backbone REPORT
 * Replicates the exact functionality from src/reactor/js/reporting_helpers.js
 */
class PDFReport {
    constructor() {
        this.pdf = null;
        this.startX = 0;
        this.startY = 0;
        this.tableStartY = 210;
        this.pageCount = 1;
    }

    /**
     * Initialize PDF Report - equivalent of REPORT.initPDFReport
     * @param {string} title - Report title
     * @param {string} orientation - 'p' for portrait, 'l' for landscape
     * @param {string} projectName - Project name to display
     * @param {boolean} isNotified - Whether notification was already shown
     * @returns {Promise} - Resolves when PDF is initialized
     */
    initPDFReport(title, orientation, projectName, isNotified = false) {
        return new Promise((resolve, reject) => {
            try {
                this.pdf = new jsPDF(orientation, 'pt', 'letter');

                // Try to load the logo - use a fallback if not available
                const logo = new Image();
                logo.crossOrigin = 'anonymous';

                logo.onload = () => {
                    try {
                        this.pdf.addImage(logo, 'PNG', 20, 20, 110, 66);
                        this.addReportHeader(title, projectName);
                        resolve();
                    } catch (error) {
                        console.warn('Could not add logo to PDF, continuing without it');
                        this.addReportHeader(title, projectName);
                        resolve();
                    }
                };

                logo.onerror = () => {
                    console.warn('Logo not found, continuing without it');
                    this.addReportHeader(title, projectName);
                    resolve();
                };

                // Try to load from relative path first, fallback to no logo
                logo.src = '/img/logo/logo_pdf_report.png';

                // Fallback timeout in case image doesn't load
                setTimeout(() => {
                    if (this.pdf && !logo.complete) {
                        console.warn('Logo loading timeout, continuing without it');
                        this.addReportHeader(title, projectName);
                        resolve();
                    }
                }, 1000);
            } catch (error) {
                reject(error);
            }
        });
    }

    addReportHeader(title, projectName) {
        this.pdf.setFontSize(12);
        this.pdf.text(220, 120, 'Report: ' + title);
        this.pdf.setFontSize(11);
        if (projectName) {
            this.pdf.text(220, 140, 'Project: ' + projectName);
        }
    }

    /**
     * Add text with colored state - equivalent of REPORT.addTextWithColoredState
     */
    addTextWithColoredState(x, y, text, isBold = false) {
        if (isBold) {
            this.pdf.setFont(undefined, 'bold');
        }
        this.pdf.text(x, y, text);
        if (isBold) {
            this.pdf.setFont(undefined, 'normal');
        }
    }

    /**
     * Check element height and add page if needed
     */
    checkElementHeightAndAddPage(currentY, margin, elementHeight) {
        const pageHeight = this.pdf.internal.pageSize.height;
        if (currentY + elementHeight + margin > pageHeight - 50) {
            this.pdf.addPage();
            this.pageCount++;
            return margin; // Return to top margin of new page
        }
        return currentY;
    }

    /**
     * Add table to PDF - equivalent of REPORT.addTable
     */
    addTable(data, columns, options = {}) {
        const startY = options.startY || this.tableStartY;

        this.pdf.autoTable({
            head: [columns],
            body: data,
            startY: startY,
            styles: {
                fontSize: 10,
                cellPadding: 4,
            },
            headStyles: {
                fillColor: [245, 245, 245],
                textColor: [0, 0, 0],
                fontStyle: 'bold',
            },
            columnStyles: options.columnStyles || {},
            didParseCell: options.didParseCell || function () {},
            margin: { top: 20, left: 20, right: 20 },
        });
    }

    /**
     * Download PDF - equivalent of REPORT.downloadPDF
     */
    downloadPDF(fileName) {
        if (this.pdf) {
            this.pdf.save(fileName);
        }
    }

    /**
     * Reset the report instance
     */
    reset() {
        this.pdf = null;
        this.startX = 0;
        this.startY = 0;
        this.tableStartY = 210;
        this.pageCount = 1;
    }
}

// Create a singleton instance to match Backbone REPORT pattern
export const REPORT = new PDFReport();

/**
 * Helper function to generate preflight PDF following Backbone logic
 */
export const generatePreflightsPDF = async (preflightComponents, selectedModels, projectName) => {
    try {
        await REPORT.initPDFReport('Source Inspection - Preflight Information', 'p', projectName);

        let startY = 210;

        // Process each preflight component (matching Backbone logic exactly)
        preflightComponents.forEach((component) => {
            if (component.type !== 'target_cloud') {
                // Get the source name from selectedModels or use host/hostName
                const sourceName =
                    component.hostName ||
                    component.host ||
                    selectedModels.find((src) => src.id === component.resource_id)?.host ||
                    'Unknown Source';

                // Display text based on errorType (or state if no errorType)
                let displayState;
                if (component.errorType) {
                    // Show "Error" for both "fatal" and "error" errorTypes
                    displayState =
                        component.errorType.toLowerCase() === 'fatal' ? 'Error' : makeNiceName(component.errorType);
                } else if (component.state === 'success') {
                    displayState = 'Ready';
                } else {
                    displayState = makeNiceName(component.state);
                }

                // Add source name and colored status
                startY = REPORT.checkElementHeightAndAddPage(startY, 40, 20);

                // Add source name in bold black
                REPORT.pdf.setFont(undefined, 'bold');
                REPORT.pdf.setTextColor(0, 0, 0); // Black
                REPORT.pdf.text(40, startY, `${sourceName}: `);

                // Calculate x position for status text (after source name)
                const sourceNameWidth = REPORT.pdf.getTextWidth(`${sourceName}: `);

                // Add status in appropriate color based on errorType (not state)
                const stateColors = {
                    fatal: [255, 0, 0], // Red for fatal errors
                    error: [255, 0, 0], // Red for errors
                    warning: [248, 148, 6], // Orange for warnings
                    success: [62, 168, 24], // Green for success
                    ready: [62, 168, 24], // Green for ready state
                };

                // Determine color based on errorType, fallback to state
                let colorKey;
                if (component.errorType) {
                    colorKey = component.errorType.toLowerCase();
                } else if (component.state?.toLowerCase() === 'success') {
                    colorKey = 'ready';
                } else {
                    colorKey = component.state?.toLowerCase() || 'error';
                }

                const color = stateColors[colorKey] || [0, 0, 0]; // Default to black if unknown

                REPORT.pdf.setTextColor(color[0], color[1], color[2]);
                REPORT.pdf.text(40 + sourceNameWidth, startY, displayState);

                // Reset to normal font and black color
                REPORT.pdf.setFont(undefined, 'normal');
                REPORT.pdf.setTextColor(0, 0, 0);

                startY += 20;

                // Extract errors from individual preflight checks
                let tableData = [];

                if (component.data?.content && Array.isArray(component.data.content)) {
                    component.data.content.forEach((check) => {
                        if (check.errors && check.errors.length > 0) {
                            // Group all errors from the same check into one row with bullet points and spacing
                            const errorMessages = check.errors.map((error) => `• ${error.message || ''}`).join('\n');

                            // Status text based on errorType (same logic as header)
                            const statusText = component.errorType
                                ? component.errorType.toLowerCase() === 'fatal'
                                    ? 'Error'
                                    : makeNiceName(component.errorType)
                                : component.state === 'success'
                                  ? 'Ready'
                                  : makeNiceName(component.state);

                            tableData.push([
                                check.label || check.name || 'Unknown Check',
                                check.description || '',
                                errorMessages,
                                statusText,
                            ]);
                        }
                    });
                }

                // If no individual check errors but component has errors, use component-level errors
                if (tableData.length === 0 && component.errors && component.errors.length) {
                    // Group all component-level errors into one row with bullet points and spacing
                    const errorMessages = component.errors.map((error) => `• ${error.message || ''}`).join('\n');
                    const errorNames = component.errors.map((error) => `• ${error.name || 'Unknown Check'}`).join('\n');
                    const errorDescriptions = component.errors
                        .map((error) => `• ${error.description || ''}`)
                        .join('\n');

                    // Status text based on errorType (same logic as header)
                    const statusText = component.errorType
                        ? component.errorType.toLowerCase() === 'fatal'
                            ? 'Error'
                            : makeNiceName(component.errorType)
                        : component.state === 'success'
                          ? 'Ready'
                          : makeNiceName(component.state);

                    tableData = [[errorNames, errorDescriptions, errorMessages, statusText]];
                }

                if (tableData.length > 0) {
                    const columns = ['Preflight Check', 'Description', 'Errors/Warnings', 'Status'];

                    startY = REPORT.checkElementHeightAndAddPage(startY, 40, tableData.length * 20 + 40);

                    REPORT.pdf.autoTable({
                        head: [columns],
                        body: tableData,
                        startY: startY,
                        styles: {
                            fontSize: 9,
                            cellPadding: 3,
                        },
                        headStyles: {
                            fillColor: [29, 162, 220],
                            textColor: [255, 255, 255],
                            fontStyle: 'bold',
                        },
                        columnStyles: {
                            0: { cellWidth: 100 },
                            1: { cellWidth: 100 },
                            3: { cellWidth: 50 },
                        },
                        didParseCell: function (data) {
                            if (
                                data.cell.section !== 'head' &&
                                (data.column.dataKey === 2 || data.column.dataKey === 3)
                            ) {
                                const stateColors = {
                                    fatal: [255, 0, 0], // Red for fatal errors
                                    error: [255, 0, 0], // Red for errors
                                    warning: [248, 148, 6], // Orange for warnings
                                    success: [62, 168, 24], // Green for success
                                    ready: [62, 168, 24], // Green for ready state
                                };

                                // Determine color based on errorType, fallback to state
                                let colorKey;
                                if (component.errorType) {
                                    colorKey = component.errorType.toLowerCase();
                                } else if (component.state?.toLowerCase() === 'success') {
                                    colorKey = 'ready';
                                } else {
                                    colorKey = component.state?.toLowerCase() || 'error';
                                }

                                if (stateColors[colorKey]) {
                                    data.cell.styles.textColor = stateColors[colorKey];
                                }
                            }
                        },
                        margin: { left: 40, right: 40 },
                    });

                    startY = REPORT.pdf.lastAutoTable.finalY + 20;
                } else {
                    startY += 20;
                }
            }
        });

        REPORT.pageCount = 1;
        REPORT.downloadPDF('Source Inspection - Preflight Information.pdf');
    } catch (error) {
        console.error('Error generating preflight PDF:', error);
        throw error;
    }
};
/**
 * Helper function to generate inventory PDF
 */
export const generateInventoryPDF = async (selectedModels, projectName) => {
    try {
        await REPORT.initPDFReport('Source Inspection - Inventory Information', 'p', projectName);

        let startY = 210;

        selectedModels.forEach((src) => {
            const isVMBased = src.collection_type === 'vm';

            // Source header - always show IP/hostname even for failed inspections
            const src_header = src.host || src.hostname || 'Unknown Source';
            startY = REPORT.checkElementHeightAndAddPage(startY, 40, 20);
            REPORT.addTextWithColoredState(40, startY, src_header, true);
            startY += 20;

            // If inspection failed, show basic error info and continue to next source
            if (!src.attributes || src.attributes.state !== 'success') {
                const errorInfoData = [
                    ['Status', 'Inspection Failed'],
                    ['IP Address', src.host || 'N/A'],
                    ['Hostname', src.hostname || 'N/A'],
                ];

                REPORT.pdf.autoTable({
                    body: errorInfoData,
                    startY: startY,
                    styles: {
                        fontSize: 10,
                        cellPadding: 4,
                    },
                    columnStyles: {
                        0: { cellWidth: 100, fontStyle: 'bold' },
                        1: { cellWidth: 'auto' },
                    },
                    margin: { left: 40, right: 40 },
                });

                startY = REPORT.pdf.lastAutoTable.finalY + 20;
                return;
            }

            // Basic information table
            const basicInfoData = [
                ['OS', src.attributes.os?.desc || 'N/A'],
                [
                    'CPU',
                    isVMBased
                        ? `${src.attributes.cpu?.processors?.length || 0}`
                        : (src.attributes.cpu?.processors || [])
                              .map((cpu) => cpu.name || cpu.model || 'Unknown')
                              .join('\n'),
                ],
                [
                    'Memory',
                    src.attributes.memory
                        ? Math.round((src.attributes.memory.total_kb / 1024 / 1024) * 100) / 100 + ' GiB RAM'
                        : 'N/A',
                ],
            ];

            // Add Disks/Mounts based on VM type
            if (isVMBased) {
                if (src.attributes.storage?.vm_disks) {
                    const disksText = src.attributes.storage.vm_disks
                        .map(
                            (disk) =>
                                `${disk.label || disk.device_id}, Size: ${Math.round((disk.capacity_kb / 1024 / 1024) * 100) / 100} GiB, ${disk.type || 'thick'}`,
                        )
                        .join('\n');
                    basicInfoData.push(['Disks', disksText]);
                }
                basicInfoData.push(['VM Name', src.vm_name || 'N/A']);
            } else {
                if (src.attributes.storage?.mounts) {
                    // Use getMountDisplayInfo to process mounts
                    const processedMounts = getMountDisplayInfo(
                        src.os_type,
                        src.attributes.storage.mounts,
                        false,
                        true,
                        false,
                        false,
                    );
                    const mountsText = processedMounts
                        .map((mount) => `${mount.mount_name} Size: ${mount.size} GiB, Free: ${mount.free} GiB`)
                        .join('\n');
                    basicInfoData.push(['Mounts', mountsText]);
                }
                basicInfoData.push(['Host Name', src.attributes.network?.hostname || 'N/A']);
            }

            // Create basic info table using jsPDF
            REPORT.pdf.autoTable({
                body: basicInfoData,
                startY: startY,
                styles: {
                    fontSize: 10,
                    cellPadding: 4,
                    overflow: 'linebreak',
                },
                columnStyles: {
                    0: { fontStyle: 'bold', cellWidth: 60 },
                    1: { cellWidth: 300 },
                },
                margin: { left: 40, right: 40 },
                theme: 'plain',
            });

            startY = REPORT.pdf.lastAutoTable.finalY + 20;

            // Installed software table (for non-VM sources only)
            if (
                !isVMBased &&
                src.attributes.os?.installed_programs &&
                src.attributes.os.installed_programs.length > 0
            ) {
                startY = REPORT.checkElementHeightAndAddPage(startY, 40, 20);
                REPORT.addTextWithColoredState(40, startY, 'Installed Software', true);
                startY += 20;

                const softwareData = src.attributes.os.installed_programs.map((software) => [
                    software.display_name || 'N/A',
                    software.publisher || 'N/A',
                    software.display_version || 'N/A',
                ]);

                // Use full page width for the table
                REPORT.pdf.autoTable({
                    head: [['Name', 'Vendor', 'Version']],
                    body: softwareData,
                    startY: startY,
                    styles: {
                        fontSize: 8,
                        cellPadding: 2,
                    },
                    headStyles: {
                        fillColor: [29, 162, 220],
                        textColor: [255, 255, 255],
                        fontStyle: 'bold',
                    },
                    columnStyles: {
                        0: { cellWidth: 'auto' }, // Use auto width for full table width
                        1: { cellWidth: 'auto' },
                        2: { cellWidth: 'auto' },
                    },
                    tableWidth: 'auto', // Full page width
                    margin: { left: 40, right: 40 },
                });

                startY = REPORT.pdf.lastAutoTable.finalY + 25;
            }

            startY += 20; // Space between sources
        });

        REPORT.pageCount = 1;
        REPORT.downloadPDF('Source Inspection - Inventory Information.pdf');
    } catch (error) {
        console.error('Error generating inventory PDF:', error);
        throw error;
    }
};
